import React from 'react';
import type { StepIconProps } from '@mui/material';
import {
  Box,
  Stack,
  Step,
  StepConnector,
  stepConnectorClasses,
  StepLabel,
  Stepper,
  styled,
  Typography,
} from '@mui/material';

interface HorizontalStepperProps {
  activeStep: number;
  edit?: boolean;
  steps: string[];
}

const GREEN = "#2E7D32";
const BLUE = "#0B5ED7";
const GRAY = "#BDBDBD";
const LINE_GRAY = "#E0E0E0";
const TEXT_MUTED = "#797979";

const CIRCLE_SIZE = 20;
const BLUE_RING = 2;
const WHITE_RING = 2;
const BLUE_CORE = CIRCLE_SIZE - (BLUE_RING + WHITE_RING) * 2;
const CHECK_SIZE = 16;
const CONNECTOR_THICK = 3;

const Connector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: { top: CIRCLE_SIZE / 2 },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: LINE_GRAY,
    borderTopWidth: CONNECTOR_THICK,
    borderRadius: 1,
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    borderColor: GREEN,
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    borderColor: BLUE,
  },
}));

const Check16 = () => (
  <svg width={CHECK_SIZE} height={CHECK_SIZE} viewBox="0 0 16 16">
    <path
      d="M3.2 8.6 L6.3 11.2 L12.8 4.8"
      fill="none"
      stroke="#FFFFFF"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function CustomStepIcon(props: StepIconProps) {
  const { active, completed } = props;

  if (completed) {
    return (
      <Box
        sx={{
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          borderRadius: '50%',
          bgcolor: GREEN,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Check16 />
      </Box>
    );
  }

  if (active) {
    return (
      <Box
        sx={{
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          borderRadius: "50%",
          border: `${BLUE_RING}px solid ${BLUE}`,
          boxSizing: "border-box",
          p: `${WHITE_RING}px`,
          bgcolor: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: BLUE_CORE,
            height: BLUE_CORE,
            borderRadius: "100%",
            bgcolor: BLUE,
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: CIRCLE_SIZE,
        height: CIRCLE_SIZE,
        borderRadius: "50%",
        bgcolor: GRAY,
      }}
    />
  );
}

export const FormSteps: React.FC<HorizontalStepperProps> = ({
  activeStep,
  edit,
  steps,
}) => {
  const current = Math.max(0, (activeStep ?? 1) - 1);

  return (
    <Stack paddingX={{ xs: 0 }}>
      <Box
        sx={{
          width: "100%",
          bgcolor: "#FFFFFF",
          px: { xs: "1rem" },
          py: "1.75rem",
          overflowX: "auto",
          overflowY: "hidden",
          "& .MuiStepper-root": { minWidth: steps.length * 200 },
        }}
      >
        <Stepper
          alternativeLabel
          activeStep={current}
          connector={<Connector />}
        >
          {steps.map((step, index) => {
            const isDone = index < current;
            const isActive = index === current;

            return (
              <Step
                key={index}
                sx={{
                  minWidth: 200,
                  whiteSpace: 'nowrap'
                }}
              >
                <StepLabel
                  StepIconComponent={CustomStepIcon}
                  sx={{
                    alignItems: 'center',
                    '& .MuiStepLabel-iconContainer': {
                      m: 0,
                      p: 0,
                      width: CIRCLE_SIZE,
                      height: CIRCLE_SIZE,
                      lineHeight: 0,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                  }}
                >
                  <Typography
                    fontWeight={400}
                    fontSize="0.75rem"
                    sx={{ color: TEXT_MUTED }}
                  >
                    ETAPA {index + 1}
                  </Typography>

                  <Typography fontWeight={600} fontSize="0.8rem">
                    {step}
                  </Typography>

                  {edit && (
                    <>
                      {isDone && (
                        <Typography fontSize="0.75rem" sx={{ color: GREEN }}>
                          preenchido
                        </Typography>
                      )}
                      {isActive && (
                        <Typography fontSize="0.75rem" sx={{ color: BLUE }}>
                          a preencher
                        </Typography>
                      )}
                      {!isDone && !isActive && (
                        <Typography
                          fontSize="0.75rem"
                          sx={{ color: "#9E9E9E" }}
                        >
                          pendente
                        </Typography>
                      )}
                    </>
                  )}
                </StepLabel>
              </Step>
            )
          })}
        </Stepper>
      </Box>
    </Stack>
  )
}
